import { useRouteMatch } from 'quartz-routing/react-router';
import { useAppContextQuery } from '../../hooks/useAppContextQuery';
import { Paths } from '../../constants/paths';
import { GET_PLACEMENTS_QUERY } from '../../../__generated__/chirp/com/hubspot/marketplace/discovery/studio/rpc/PlacementsRpc.dfc.new';
import { GET_PLACEMENTS_QUERY as PUBLIC_GET_PLACEMENTS_QUERY } from '../../../__generated__/chirp/com/hubspot/marketplace/discovery/studio/rpc/PlacementsRpc-external.dfc.new';
import {
  CalloutPlacementDiscriminant,
  CollectionPlacementDiscriminant,
  type CalloutPlacement,
  type CollectionPlacement,
  type Placement,
} from '../../../__generated__/chirp/com/hubspot/marketplace/discovery/studio/rpc/PlacementsRpc';
import { PAGE_CATALOG } from 'marketplace-discovery-studio-lib/placements/catalog';
import type { PageId } from 'marketplace-discovery-studio-lib/placements/types';

export type { CalloutPlacement, CollectionPlacement, PageId, Placement };

export type PlacementsBySection = Record<string, Placement>;

/**
 * Legacy hero CTA behaviors that aren't part of the studio callout model yet.
 * Read these off a placement's `calloutContent` via {@link getCalloutCta}.
 */
export interface LegacyCalloutCta {
  openCopilot?: boolean;
  openFindPartnerMatch?: boolean;
  secondaryButtonText?: string;
  secondaryButtonUrl?: string;
}

/** Reads the legacy CTA fields off a callout placement's content. */
export function getCalloutCta(placement: CalloutPlacement): LegacyCalloutCta {
  return placement.calloutContent as LegacyCalloutCta;
}

const PATH_TO_PAGE_ID: Partial<Record<string, PageId>> = {
  [Paths.HOME]: 'HOME',
  [Paths.APPS]: 'APPS',
  [Paths.AGENTS]: 'AGENTS',
  [Paths.FEATURED_COLLECTION]: 'FEATURED_COLLECTION',
  [Paths.SOLUTIONS_PARTNERS]: 'SOLUTIONS_PARTNERS',
  [Paths.WORKFLOW_TEMPLATES]: 'WORKFLOWS',
};

/** Maps the active route to a placements `PageId` (or undefined if unmapped). */
export function useCurrentPageId(): PageId | undefined {
  const currentMatch = useRouteMatch();
  return currentMatch?.path ? PATH_TO_PAGE_ID[currentMatch.path] : undefined;
}

/**
 * Ordered banner callout placements (`BANNER_1`, `BANNER_2`, ...) for the
 * current page. Used to render banners interleaved between page sections
 * rather than stacked under the top callout row.
 */
export const BANNER_SECTIONS = ['BANNER_1', 'BANNER_2'] as const;

export function isCalloutPlacement(p: Placement): p is CalloutPlacement {
  return p.entityType === CalloutPlacementDiscriminant || 'calloutContent' in p;
}

export function isCollectionPlacement(p: Placement): p is CollectionPlacement {
  return (
    p.entityType === CollectionPlacementDiscriminant || 'collectionPreview' in p
  );
}

/**
 * Ordered collection section ids for a placements page, derived from the
 * Discovery Studio page catalog so new collection slots are picked up
 * automatically.
 */
export function getCollectionSectionsForPage(
  pageId: PageId | undefined
): readonly string[] {
  if (!pageId) {
    return [];
  }

  const page = PAGE_CATALOG[pageId];
  if (!page) {
    return [];
  }

  return page.sections
    .filter((section) => section.allowedEntityTypes.includes('COLLECTION'))
    .map((section) => section.id);
}

/** Ordered collection placements for the given section ids (skips empty slots). */
export function getCollectionPlacementsForSections(
  placementsBySection: PlacementsBySection,
  sections: readonly string[]
): CollectionPlacement[] {
  return sections
    .map((section) => placementsBySection[section])
    .filter(
      (placement): placement is CollectionPlacement =>
        Boolean(placement) && isCollectionPlacement(placement)
    );
}

/** Inlined collection previews for the given section ids (skips empty slots). */
export function getCollectionPreviewsForSections(
  placementsBySection: PlacementsBySection,
  sections: readonly string[]
) {
  return getCollectionPlacementsForSections(placementsBySection, sections).map(
    (placement) => placement.collectionPreview
  );
}

/** Collection previews for the catalog collection slots on a page. */
export function getPageCollectionPreviews(
  placementsBySection: PlacementsBySection,
  pageId: PageId | undefined
) {
  return getCollectionPreviewsForSections(
    placementsBySection,
    getCollectionSectionsForPage(pageId)
  );
}

export function usePageCalloutBanners(): CalloutPlacement[] {
  const pageId = useCurrentPageId();
  const { placementsBySection } = usePagePlacements(pageId);
  return BANNER_SECTIONS.map((section) => placementsBySection[section]).filter(
    (placement): placement is CalloutPlacement =>
      Boolean(placement) && isCalloutPlacement(placement)
  );
}

interface UsePagePlacementsResult {
  placementsBySection: PlacementsBySection;
  loading: boolean;
  error: unknown;
}

function toPlacementsBySection(
  placements: readonly Placement[]
): PlacementsBySection {
  const placementsBySection: PlacementsBySection = {};
  for (const placement of placements) {
    placementsBySection[placement.section] = placement;
  }
  return placementsBySection;
}

/** Returns published placements for the given storefront page. */
export function usePagePlacements(
  pageId: PageId | undefined
): UsePagePlacementsResult {
  const { data, loading, error } = useAppContextQuery({
    app: GET_PLACEMENTS_QUERY,
    public: PUBLIC_GET_PLACEMENTS_QUERY,
    options: {
      skip: !pageId,
      variables: { page: pageId, published: true },
    },
  });

  const placements = data?.result?.placements ?? [];

  return {
    placementsBySection: toPlacementsBySection(placements),
    loading,
    error,
  };
}
